import React, { useState, useEffect } from 'react';
import useFurnitureStore from '../store/useFurnitureStore';
import { getItemById } from '../catalog/catalogManager';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';

const { FiTrash2, FiRotateCw, FiCopy, FiArrowUp, FiRepeat, FiLock, FiUnlock } = FiIcons;

const FurniturePropertiesPanel = () => {
  const { 
    selectedFurnitureId, 
    furnitureItems, 
    updateFurnitureItem, 
    rotateFurnitureItem, 
    removeFurnitureItem, 
    duplicateFurnitureItem,
    flipFurnitureItem,
    bringFurnitureToFront
  } = useFurnitureStore();
  
  const [localWidth, setLocalWidth] = useState(0);
  const [localDepth, setLocalDepth] = useState(0);
  const [localRotation, setLocalRotation] = useState(0);
  
  const selectedFurniture = selectedFurnitureId ? furnitureItems[selectedFurnitureId] : null;
  const furnitureTemplate = selectedFurniture ? getItemById(selectedFurniture.itemId) : null;

  useEffect(() => {
    if (selectedFurniture && furnitureTemplate) {
      setLocalWidth(Math.round(selectedFurniture.width));
      setLocalDepth(Math.round(selectedFurniture.depth));
      setLocalRotation(Math.round(selectedFurniture.rotation * 180 / Math.PI));
    }
  }, [selectedFurniture, furnitureTemplate]);

  if (!selectedFurniture || !furnitureTemplate) return null;

  const handleRotationChange = (deg) => {
    let val = parseInt(deg);
    if (isNaN(val)) val = 0;
    setLocalRotation(val);
    updateFurnitureItem(selectedFurnitureId, { rotation: val * Math.PI / 180 });
  };

  const handleDimensionChange = (dimension, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue <= 0) return;
    if (dimension === 'width') { setLocalWidth(numValue); updateFurnitureItem(selectedFurnitureId, { width: numValue }); }
    else if (dimension === 'depth') { setLocalDepth(numValue); updateFurnitureItem(selectedFurnitureId, { depth: numValue }); }
  };

  const currentFillColor = selectedFurniture.fillColor || furnitureTemplate.render2D?.fill || '#ffffff';
  const currentStrokeColor = selectedFurniture.strokeColor || furnitureTemplate.render2D?.stroke || '#000000';
  const currentStrokeWidth = selectedFurniture.strokeWidth !== undefined ? selectedFurniture.strokeWidth : (furnitureTemplate.render2D?.strokeWidth || 1);

  return (
    <div className="absolute left-20 top-20 w-64 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden z-10">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 text-center">
        <h3 className="font-semibold text-gray-700 text-sm">Właściwości Obiektu</h3>
      </div>
      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-2xl">{furnitureTemplate.thumbnail}</div>
          <div className="flex-1">
            <div className="font-medium text-gray-900 text-sm truncate">{furnitureTemplate.name}</div>
          </div>
        </div>

        {/* Orientacja */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Orientacja</div>
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={() => flipFurnitureItem(selectedFurnitureId, 'horizontal')}
              className={clsx("flex flex-col items-center gap-1 p-2 border rounded-lg transition-all", selectedFurniture.flipX ? "bg-indigo-50 border-indigo-200 text-indigo-600" : "hover:bg-gray-50 border-gray-200 text-gray-600")}
            >
              <SafeIcon icon={FiRepeat} className="text-lg" />
              <span className="text-[10px] font-medium uppercase">Odbij Poziom</span>
            </button>
            <button 
              onClick={() => flipFurnitureItem(selectedFurnitureId, 'vertical')}
              className={clsx("flex flex-col items-center gap-1 p-2 border rounded-lg transition-all", selectedFurniture.flipY ? "bg-indigo-50 border-indigo-200 text-indigo-600" : "hover:bg-gray-50 border-gray-200 text-gray-600")}
            >
              <SafeIcon icon={FiRepeat} className="text-lg rotate-90" />
              <span className="text-[10px] font-medium uppercase">Odbij Pion</span>
            </button>
          </div>
        </div>

        {/* Wymiary */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Wymiary (cm)</div>
            <button 
              onClick={() => updateFurnitureItem(selectedFurnitureId, { lockAspectRatio: !selectedFurniture.lockAspectRatio })}
              className={clsx("p-1 rounded transition-colors", selectedFurniture.lockAspectRatio ? "text-indigo-600 bg-indigo-50" : "text-gray-400 hover:bg-gray-100")}
            >
              <SafeIcon icon={selectedFurniture.lockAspectRatio ? FiLock : FiUnlock} className="text-sm" />
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs text-gray-600 w-16">Szerokość:</label>
              <input type="number" value={localWidth} onChange={(e) => handleDimensionChange('width', e.target.value)} className="h-8 w-20 rounded border border-gray-300 px-2 text-sm text-right focus:border-indigo-500 focus:outline-none" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs text-gray-600 w-16">Głębokość:</label>
              <input type="number" value={localDepth} onChange={(e) => handleDimensionChange('depth', e.target.value)} className="h-8 w-20 rounded border border-gray-300 px-2 text-sm text-right focus:border-indigo-500 focus:outline-none" />
            </div>
          </div>
        </div>

        {/* Obrót */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Obrót (°)</label>
            <div className="flex gap-1">
               <button onClick={() => rotateFurnitureItem(selectedFurnitureId, -Math.PI/4)} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded hover:bg-gray-200">-45°</button>
               <button onClick={() => rotateFurnitureItem(selectedFurnitureId, Math.PI/4)} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded hover:bg-gray-200">+45°</button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="number" value={localRotation} onChange={(e) => handleRotationChange(e.target.value)} className="h-8 w-16 rounded border border-gray-300 px-2 text-sm text-center" />
            <input type="range" min="0" max="360" value={localRotation % 360} onChange={(e) => handleRotationChange(e.target.value)} className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
          </div>
        </div>

        {/* Wygląd */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Wygląd</div>
          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-600">Grubość obrysu:</label>
              <div className="flex items-center gap-2">
                <input type="range" min="0.1" max="10" step="0.1" value={currentStrokeWidth} onChange={(e) => updateFurnitureItem(selectedFurnitureId, { strokeWidth: parseFloat(e.target.value) })} className="w-20 h-2 bg-gray-200 rounded-lg accent-indigo-600" />
                <span className="text-xs font-mono w-6 text-right">{currentStrokeWidth}</span>
              </div>
            </div>
            <div className="flex items-center justify-around pt-1">
              <div className="flex flex-col items-center gap-1">
                <label className="text-[10px] text-gray-500">Wypełnienie</label>
                <input type="color" value={currentFillColor} onChange={(e) => updateFurnitureItem(selectedFurnitureId, { fillColor: e.target.value })} className="w-8 h-8 rounded cursor-pointer border border-gray-300 p-0.5" />
              </div>
              <div className="flex flex-col items-center gap-1">
                <label className="text-[10px] text-gray-500">Obrys</label>
                <input type="color" value={currentStrokeColor} onChange={(e) => updateFurnitureItem(selectedFurnitureId, { strokeColor: e.target.value })} className="w-8 h-8 rounded cursor-pointer border border-gray-300 p-0.5" />
              </div>
            </div>
          </div>
        </div>

        <div className="pt-2 border-t border-gray-200 space-y-2">
          <button onClick={() => duplicateFurnitureItem(selectedFurnitureId)} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"><SafeIcon icon={FiCopy} /> Duplikuj</button>
          <button onClick={() => bringFurnitureToFront(selectedFurnitureId)} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"><SafeIcon icon={FiArrowUp} /> Przesuń na górę</button>
          <button onClick={() => removeFurnitureItem(selectedFurnitureId)} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"><SafeIcon icon={FiTrash2} /> Usuń</button>
        </div>
      </div>
    </div>
  );
};

export default FurniturePropertiesPanel;